<template>
    <button
        :type="type"
        :disabled="isDisabled"
        class="NewButton"
        :class="[
            `NewButton--${variant}`,
            `NewButton--${color}`,
            `NewButton--${size}`,
            { 'NewButton--disabled': isDisabled },
        ]"
        v-bind="$attrs"
    >
        <div class="NewButton__content">
            <EidIcon v-if="loading" class="animate-spin" :icon-src="faSpinner" />
            <slot />
        </div>
    </button>
</template>

<style lang="scss">
    @use "sass:map";

    $button-sizes: (
        lg: (
            height: 48px,
            padding: var(--control-size-lg-padding),
            font-size: var(--control-size-lg-font-size),
            line-height: var(--control-size-lg-line-height),
        ),
        default: (
            height: 40px,
            padding: var(--control-size-default-padding),
            font-size: var(--control-size-default-font-size),
            line-height: var(--control-size-default-line-height),
        ),
        sm: (
            height: 36px,
            padding: var(--control-size-sm-padding),
            font-size: var(--control-size-sm-font-size),
            line-height: var(--control-size-sm-line-height),
        ),
        xs: (
            height: 32px,
            padding: var(--control-size-xs-padding),
            font-size: var(--control-size-xs-font-size),
            line-height: var(--control-size-xs-line-height),
        ),
        icon: (
            height: 40px,
            width: 40px,
        ),
        icon-sm: (
            height: 36px,
            width: 36px,
        ),
        icon-xs: (
            height: 32px,
            width: 32px,
        ),
        icon-xxs: (
            height: 1.125rem,
            width: 1.125rem,
        ),
    );

    .NewButton {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        border: 1px solid transparent;
        white-space: nowrap;
        transition: all 0.2s ease-in-out;
        outline: none;
        font-weight: 500;
        border-radius: 0.5rem;
        cursor: pointer;
        font-family: inherit;

        &--lg {
            height: map.get($button-sizes, lg, height);
            padding: map.get($button-sizes, lg, padding);
            font-size: map.get($button-sizes, lg, font-size);
            line-height: map.get($button-sizes, lg, line-height);
        }

        &--default {
            height: map.get($button-sizes, default, height);
            padding: map.get($button-sizes, default, padding);
            font-size: map.get($button-sizes, default, font-size);
            line-height: map.get($button-sizes, default, line-height);
        }

        &--sm {
            height: map.get($button-sizes, sm, height);
            padding: map.get($button-sizes, sm, padding);
            font-size: map.get($button-sizes, sm, font-size);
            line-height: map.get($button-sizes, sm, line-height);
        }

        &--xs {
            height: map.get($button-sizes, xs, height);
            padding: map.get($button-sizes, xs, padding);
            font-size: map.get($button-sizes, xs, font-size);
            line-height: map.get($button-sizes, xs, line-height);
        }

        &--icon {
            height: map.get($button-sizes, icon, height);
            width: map.get($button-sizes, icon, width);
        }

        &--icon-sm {
            height: map.get($button-sizes, icon-sm, height);
            width: map.get($button-sizes, icon-sm, width);
        }

        &--icon-xs {
            height: map.get($button-sizes, icon-xs, height);
            width: map.get($button-sizes, icon-xs, width);
            padding: 0;
        }

        &--icon-xxs {
            height: map.get($button-sizes, icon-xxs, height);
            width: map.get($button-sizes, icon-xxs, width);
            font-size: 0.625rem;
            border-radius: 0.25rem;
        }

        &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 2px transparent, 0 0 0 4px currentColor;
        }

        &__content {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        // Fill variant styles
        &--fill {
            &.NewButton--primary {
                color: var(--eidWhite);
                background-color: var(--eidTrustGreen);
                border-color: var(--eidTrustGreen);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidTrustGreen-75);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTrustGreen);
                }
            }

            &.NewButton--secondary {
                color: var(--eidWhite);
                background-color: var(--eidSecureBlue);
                border-color: var(--eidSecureBlue);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidSecureBlue-75);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidSecureBlue);
                }
            }

            &.NewButton--danger {
                color: var(--eidWhite);
                background-color: var(--eidDangerRed);
                border-color: var(--eidDangerRed);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidDangerRed-75);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidDangerRed);
                }
            }

            &.NewButton--neutral {
                background-color: var(--eidTechGray);
                color: var(--eidWhite);
                border-color: var(--eidTechGray);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidTechGray-75);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTechGray);
                }
            }
        }

        // Outline variant styles
        &--outline {
            background-color: var(--eidWhite);

            &.NewButton--primary {
                color: var(--eidTrustGreen);
                border-color: var(--eidTrustGreen);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidTrustGreen-10);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTrustGreen);
                }
            }

            &.NewButton--secondary {
                color: var(--eidSecureBlue);
                border-color: var(--eidSecureBlue);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidSecureBlue-10);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidSecureBlue);
                }
            }

            &.NewButton--danger {
                color: var(--eidDangerRed);
                border-color: var(--eidDangerRed);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidDangerRed-10);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidDangerRed);
                }
            }

            &.NewButton--neutral {
                color: var(--eidTechGray);
                border-color: var(--eidTechGray);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidTechGray-10);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTechGray);
                }
            }
        }

        // Disabled state
        &--disabled {
            cursor: not-allowed;
            opacity: 0.3 !important;
            pointer-events: none;
        }

        // Link variant styles (no background, underline on hover)
        &--link {
            background-color: transparent;
            border-color: transparent;
            text-decoration: none;
            padding: 8px 4px;

            &:hover:not(.NewButton--disabled) {
                text-underline-offset: 0.3em;
            }

            &.NewButton--primary {
                color: var(--eidTrustGreen);

                &:hover:not(.NewButton--disabled) {
                    text-decoration: underline;
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTrustGreen);
                }
            }

            &.NewButton--secondary {
                color: var(--eidSecureBlue);

                &:hover:not(.NewButton--disabled) {
                    text-decoration: underline;
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidSecureBlue);
                }
            }

            &.NewButton--danger {
                color: var(--eidDangerRed);

                &:hover:not(.NewButton--disabled) {
                    text-decoration: underline;
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidDangerRed);
                }
            }

            &.NewButton--neutral {
                color: var(--eidTechGray);

                &:hover:not(.NewButton--disabled) {
                    text-decoration: underline;
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTechGray);
                }
            }
        }

        // Flat variant styles (text-only, no border/background)
        &--flat {
            background-color: transparent;
            border-color: transparent;

            &.NewButton--primary {
                color: var(--eidTrustGreen);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidTrustGreen-10);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTrustGreen);
                }
            }

            &.NewButton--secondary {
                color: var(--eidSecureBlue);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidSecureBlue-10);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidSecureBlue);
                }
            }

            &.NewButton--danger {
                color: var(--eidDangerRed);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidDangerRed-10);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidDangerRed);
                }
            }

            &.NewButton--neutral {
                color: var(--eidTechGray);

                &:hover:not(.NewButton--disabled) {
                    background-color: var(--eidTechGray-10);
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTechGray);
                }
            }
        }

        &--gradient {
            &.NewButton--primary {
                background: linear-gradient(var(--eidSafetyBlue), var(--eidTrustGreen));
                color: var(--eidWhite);

                &:hover:not(.NewButton--disabled) {
                    background: linear-gradient(var(--eidSafetyBlue-75), var(--eidTrustGreen-75));
                }

                &:focus-visible {
                    box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTrustGreen);
                }
            }
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faSpinner } from '@fortawesome/free-solid-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { ComponentSize } from '@evidentid/tprm-portal-lib/models/common';

    // Gradient variant currently only support primary color
    type ButtonVariant = 'fill' | 'outline' | 'flat' | 'link' | 'gradient';
    type ButtonColor = 'primary' | 'secondary' | 'danger' | 'neutral';
    type ButtonType = 'button' | 'submit' | 'reset';
    type ButtonSize = ComponentSize | 'icon' | 'icon-sm' | 'icon-xs' | 'icon-xxs';

    export default defineComponent({
        name: 'NewButton',
        components: { EidIcon },
        props: {
            variant: {
                type: String as PropType<ButtonVariant>,
                default: 'fill',
            },
            color: {
                type: String as PropType<ButtonColor>,
                default: 'primary',
            },
            type: {
                type: String as PropType<ButtonType>,
                default: 'button',
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            size: {
                type: String as PropType<ButtonSize>,
                default: 'default',
                validator: (value: string) =>
                    [ 'xs', 'sm', 'default', 'lg', 'icon', 'icon-sm', 'icon-xs', 'icon-xxs' ].includes(value),
            },
            loading: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        data() {
            return { faSpinner };
        },
        computed: {
            isDisabled(): boolean {
                return this.disabled || this.loading;
            },
        },
    });
</script>
